import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["arrowRef", "children", "icon", "shouldTakeFocus"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { STATUS_TYPES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { statusIconAppearance } from '../../util/statusIconAppearance';
import { PopoverArrow } from '../PopoverArrow/PopoverArrow';
import { PopoverIcon } from '../PopoverIcon/PopoverIcon';
import { PopoverGlobalStyle, StyledPopover } from './Popover.styles';
var PopoverComponent = function PopoverComponent(props) {
  var arrowRef = props.arrowRef,
    children = props.children,
    icon = props.icon,
    shouldTakeFocus = props.shouldTakeFocus,
    rest = _objectWithoutProperties(props, _excluded);
  var content = children;
  if (typeof children === 'function') {
    content = children(props);
  }
  var iconContent = icon;
  if (typeof iconContent === 'function') {
    iconContent = iconContent(props);
  }
  if (iconContent) {
    iconContent = /*#__PURE__*/React.createElement(PopoverIcon, null, iconContent);
  }
  return /*#__PURE__*/React.createElement(StyledPopover, _extends({
    shouldTakeFocus: false,
    icon: iconContent
  }, rest), content, iconContent, /*#__PURE__*/React.createElement(PopoverArrow, {
    ref: arrowRef
  }));
};

/**
 * You will almost always want to use this in conjunction with the `Popper` component.
 *
 * @related ['Dialog', 'Heading', 'Popper', 'TriggerButton', 'TriggerText']
 * @extends Modal
 * @since 7.0.0
 */
export var Popover = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  return /*#__PURE__*/React.createElement(PopoverComponent, _extends({
    innerRef: forwardedRef || undefined
  }, props));
});
Popover.displayName = 'Popover';
Popover.propTypes = {
  /**
   * Change the appearance of the popover. By default there is no appearance.
   */
  appearance: PropTypes.oneOf(Object.values(STATUS_TYPES)),
  /**
   * A [React Ref](https://reactjs.org/docs/refs-and-the-dom.html) that will be given to the
   * `PopoverArrow` node. This is usually provided by the `Popper` component.
   */
  arrowRef: CustomPropTypes.ref,
  /**
   * The Dialog `children` content amended with a `PopoverArrow` and global style.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The `Modal` `globalStyle` node.
   */
  globalStyle: PropTypes.node,
  /**
   * A `Heading` is **required** to give context
   * about the popover content. This is especially helpful for `modal` popovers where there
   * is no arrow to give context.
   */
  header: PropTypes.node,
  /**
   * The icon node. By default, this will use an icon based on the popover `appearance`, using no
   * icon if there is not an appearance.
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The Dialog `shouldTakeFocus` prop is always set
   * to false as focus for popovers is handled by the
   * `Popper` component.
   */
  shouldTakeFocus: PropTypes.bool,
  /**
   * When true, the popover will behave like a modal.
   *
   * Supports inline media query objects.
   */
  modal: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * @deprecated Use inline media query objects
   */
  mediaQuery: CustomPropTypes.mediaQuery
};
Popover.defaultProps = {
  icon: statusIconAppearance,
  shouldTakeFocus: false,
  globalStyle: /*#__PURE__*/React.createElement(PopoverGlobalStyle, null),
  modal: {
    sm_lte: true
  }
};